import { ToastProgrammatic as Toast } from 'buefy';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';

import { downloadOrdersStatisticsReport } from '@/api/reports';
import {
  GET_GROUP_BUYING_REPORT_LIST,
  SET_MEMBER_REPORT_RANGE,
  SET_SEARCH_DATE_TYPE,
} from '@/store/reports/constants';
import {
  DEFAULT_PER_PAGE,
  FIRST_PAGE,
  PER_PAGE_OPTIONS,
  SNACKBAR_TYPES,
} from '@/utils/constants';

import GroupBuyingsList from '../GroupBuyingsList/GroupBuyingsList.vue';

const DATE_SELECTION_OPTIONS = {
  LAST_WEEK: 0,
  THIS_WEEK: 1,
  THIS_MONTH: 2,
  SELF_DEFINE: 3,
};

const FILTER_TAG_TYPES = {
  GROUP_BUYING_STATUS: 'status',
  GROUP_BUYING_TYPE: 'type',
};

export default {
  name: 'GroupBuyingStatistics',
  components: {
    GroupBuyingsList,
  },
  props: {
    action: {
      type: String,
      default: 'list',
    },
  },
  data() {
    return {
      activeTab: 'groupBuyings',
      currentFilter: {},
      dates: [],
      dateType: 'start',
      exportBtnLoading: false,
      filter: {
        type: 'store',
        value: null,
        tags: [],
      },
      filterTagItems: [
        {
          label: '開放訂購中',
          type: FILTER_TAG_TYPES.GROUP_BUYING_STATUS,
          value: 0,
        },
        {
          label: '已截止',
          type: FILTER_TAG_TYPES.GROUP_BUYING_STATUS,
          value: 1,
        },
        {
          label: '已完成',
          type: FILTER_TAG_TYPES.GROUP_BUYING_TYPE,
          value: 2,
        },
        {
          label: '已取消',
          type: FILTER_TAG_TYPES.GROUP_BUYING_TYPE,
          value: 3,
        },
      ],
      filterTypes: [
        {
          label: '店家名稱',
          key: 'store',
        },
        {
          label: '訂購標籤',
          key: 'tag',
        },
        {
          label: '訂購名稱',
          key: 'name',
        },
        {
          label: '品項類別',
          key: 'category',
        },
        {
          label: '訂購ID',
          key: 'id',
        },
        {
          label: '例行性ID',
          key: 'schedule',
        },
      ],
      queryStatus: [],
      isChangingDate: false,
      isFiltered: false,
      maxDate: null,
      minDate: null,
      openedFilter: false,
      page: FIRST_PAGE,
      perPage: DEFAULT_PER_PAGE,
      reports: [],
      sortField: 'start',
      sortOrder: 'desc',
    };
  },
  computed: {
    ...mapGetters('reports', ['dateTypeWithRange']),
    ...mapState('reports', {
      list: ({ groupBuyingReportList }) => groupBuyingReportList,
      dateRange: ({ memberReportRange }) => memberReportRange,
      loading: ({ gettingGroupBuyingReportList }) => gettingGroupBuyingReportList,
      searchDateType: ({ searchDateType }) => searchDateType,
    }),
    allReports() {
      return this.list?.data?.records || [];
    },
    currentFilterLabel() {
      const { type } = this.filter;

      if (!type) {
        return '選擇類別';
      }
      return this.filterTypes.filter((filter) => filter.key === type)[0].label;
    },
    disableExport() {
      return !this.formattedReports?.length || this.isExporting;
    },
    filterValue() {
      if (this.filter.type === 'tag') {
        return this.filter.tags;
      }
      return this.filter.value;
    },
    formattedReports() {
      const {
        commaFormat,
        priceFormat,
      } = this.$options?.filters || {};
      return this.reports?.map((report) => {
        const {
          outOfSubsidy,
          paid,
          received,
          subsidyTotal,
          total,
          unreceived,
          ...props
        } = report;

        return {
          received: commaFormat(received),
          total: priceFormat(commaFormat(total)),
          unpaid: paid === null ? '-' : priceFormat(commaFormat(total - paid)),
          unreceived: commaFormat(unreceived),
          outOfSubsidy: priceFormat(outOfSubsidy),
          subsidyTotal: priceFormat(subsidyTotal),
          ...props,
        };
      }) || [];
    },
    infos() {
      return this.list?.data?.info || {};
    },
    isExporting() {
      return this.exportBtnLoading;
    },
    isSubmitDisabled() {
      const { type, value, tags } = this.filter;

      if (!type) {
        return false;
      }
      if (type === 'tag') {
        return !tags?.length;
      }
      return !value;
    },
    pagination() {
      return this.list?.pagination || {};
    },
    perPageOptions() {
      return PER_PAGE_OPTIONS;
    },
    total() {
      return this.pagination?.total || 0;
    },
    totalOrderedCount() {
      return this.infos?.orderCount || 0;
    },
    totalAmount() {
      const { total = 0 } = this.infos;
      const {
        commaFormat,
        priceFormat,
      } = this.$options?.filters || {};
      return priceFormat(commaFormat(total));
    },
    totalOrderCount() {
      return this.infos?.groupBuyingCount || 0;
    },
    hasDateRange() {
      const [startDate, endDate] = this.dates;
      return !!startDate && !!endDate;
    },
  },
  beforeDestroy() {
    Object.assign(this.$data, this.$options.data.apply(this));
  },
  mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.$nextTick(() => {
      this.dateType = this.searchDateType;
      this.dates = this.dateRange?.length ? this.dateRange : [];
      this.loadData();
    });
  },
  watch: {
    allReports: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.reports = cloneDeep(newVal);
        }
      },
    },
    dates(newVal, oldVal) {
      if (
        newVal?.length
        && JSON.parse(JSON.stringify(newVal)) !== JSON.parse(JSON.stringify(oldVal))
      ) {
        this.setReportRange(newVal);
      }
    },
    dateType(newVal) {
      this.setSearchDateType(newVal || 'start');
    },
    dateTypeWithRange(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.setPageOrGetData();
      }
    },
    page() {
      this.loadData();
    },
    perPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.setPageOrGetData();
      }
    },
  },
  methods: {
    ...mapActions('reports', {
      getReportList: GET_GROUP_BUYING_REPORT_LIST,
    }),
    ...mapMutations('reports', {
      setReportRange: SET_MEMBER_REPORT_RANGE,
      setSearchDateType: SET_SEARCH_DATE_TYPE,
    }),
    getParams() {
      const [start, end] = this.dates;
      const { type } = this.filter;
      const filterParam = {};
      if (type && this.filterValue?.length) {
        filterParam.filter_type = type;
        filterParam.names = this.filterValue;
      } else {
        this.isFiltered = false;
      }

      return {
        date_type: this.dateType,
        end_date: dayjs(end).endOf('day').format('YYYY-MM-DD HH:mm'),
        order: this.sortOrder,
        page: this.page,
        per_page: this.perPage,
        query_status: this.queryStatus,
        sort_by: this.sortField,
        start_date: dayjs(start).startOf('day').format('YYYY-MM-DD HH:mm'),
        ...filterParam,
      };
    },
    async loadData() {
      if (!this.hasDateRange) {
        return;
      }

      try {
        const params = this.getParams();
        await this.getReportList(params);
      } catch (ex) {
        const { message = '' } = ex;

        Toast.open({
          message: `讀取報表時發生錯誤，請稍後再試（${message}）`,
          type: 'is-danger',
        });
      }
    },
    dateFormatter(arr) {
      return arr.map((time) => this.formatTime(time, 'YYYY/MM/DD')).join(' - ');
    },
    async exportReport() {
      try {
        const params = this.getParams();
        this.exportBtnLoading = true;
        const res = await downloadOrdersStatisticsReport(params);
        const { content = '', filename } = res || {};
        const blob = new Blob([content], { type: 'text/csv' });

        saveAs(blob, filename);
      } catch (ex) {
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '下載失敗',
          msg: ex.message || '下載時發生未知錯誤，請再試一次',
        });
      } finally {
        this.exportBtnLoading = false;
      }
    },
    getPeriodWith(period = 'month', date = dayjs()) {
      return {
        end: date.endOf(period),
        start: date.startOf(period),
      };
    },
    getPeriodWithDateFormat(period = 'month', time = dayjs()) {
      const { end, start } = this.getPeriodWith(period, time);
      return [dayjs(start).toDate(), dayjs(end).toDate()];
    },
    getPeriodWithFormat(period = 'month', format = 'YYYY-MM-DD') {
      const { end, start } = this.getPeriodWith(period);

      return {
        end: this.formatTime(end, format),
        start: this.formatTime(start, format),
      };
    },
    getSelectionPeriod(start, end) {
      const [monthStart, monthEnd] = this.getPeriodWithDateFormat('month');
      if (dayjs(start).isSame(monthStart) && dayjs(end).isSame(monthEnd)) {
        return DATE_SELECTION_OPTIONS.THIS_MONTH;
      }
      const [weekStart, weekEnd] = this.getPeriodWithDateFormat('week');
      if (dayjs(start).isSame(weekStart) && dayjs(end).isSame(weekEnd)) {
        return DATE_SELECTION_OPTIONS.THIS_WEEK;
      }
      const [lastWeekStart, lastWeekEnd] = this.getPeriodWithDateFormat('week', dayjs().subtract(1, 'week'));
      if (dayjs(start).isSame(lastWeekStart) && dayjs(end).isSame(lastWeekEnd)) {
        return DATE_SELECTION_OPTIONS.THIS_WEEK;
      }

      return DATE_SELECTION_OPTIONS.SELF_DEFINE;
    },
    formatTime(time, format = 'YYYY-MM-DD') {
      return dayjs(time).format(format);
    },
    setFilterType(type) {
      this.filter.value = null;
      this.filter.tags = [];
      this.filter.type = type;
    },
    filterTagIsActive(item) {
      return this.queryStatus.includes(item.value);
    },
    onEndChanges() {
      this.isChangingDate = false;
    },
    onSelectItem(item) {
      const index = this.queryStatus.indexOf(item.value);
      if (index !== -1) {
        this.queryStatus.splice(index, 1);
      } else {
        this.queryStatus.push(item.value);
      }

      this.page = FIRST_PAGE;
    },
    onStartChanges(date) {
      this.isChangingDate = true;
      this.maxDate = dayjs(date).add(6, 'month').toDate();
      this.minDate = dayjs(date).subtract(6, 'month').toDate();
    },
    onSort(field, order) {
      this.sortField = field;
      this.sortOrder = order;
      this.loadData();
    },
    onTabChange() {
      this.$router.push('/statistics/members');
    },
    async onSearch() {
      const { type } = this.filter;

      if (!type || !this.filterValue?.length) {
        return;
      }

      try {
        this.isFiltered = true;
        this.currentFilter = JSON.parse(JSON.stringify(this.filter));
        this.setPageOrGetData();
      } catch (ex) {
        const { message = '' } = ex;

        Toast.open({
          message: `讀取報表時發生錯誤，請稍後再試（${message}）`,
          type: 'is-danger',
        });
        this.isFiltered = false;
      }
    },
    async onReset() {
      this.isFiltered = false;
      this.currentFilter = {};
      this.filter = {
        type: 'store',
        value: null,
        tags: [],
      };
      this.setPageOrGetData();
    },
    setPageOrGetData() {
      this.reports = [];
      if (this.page !== FIRST_PAGE) {
        this.page = FIRST_PAGE;
      } else {
        this.loadData();
      }
    },
  },
};
